/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {
  VehicleScenarioSelectPopUpComponent
} from '../../../core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component';
import {getDefaultYearRange} from '../../../core/helpers/generic-helpers';
import {StateService} from '../../../core/state/state.service';
import {VarianceMktDivSgmntRptRequest} from '../../../models/reports/variance-mkt-div-sgmnt-rpt-request.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {ReportService} from '../../../services/report.service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-variance-mkt-div-sgmnt-rpt',
    templateUrl: './variance-mkt-div-sgmnt-rpt.component.html',
    styleUrl: './variance-mkt-div-sgmnt-rpt.component.scss',
    standalone: false
})
export class VarianceMktDivSgmntRptComponent implements OnInit {
  showSpinner: boolean;
  isRptByPlngMkt: boolean = false;

  workingScenario: VehicleScenario;
  compareScenario: VehicleScenario;

  allFilterOptions: any[] = [];
  filteredData: any[] = [];
  selectedGeography: number;
  // Geography flags
  SUBREGION_FLAG = 1;
  MKTAREA_FLAG = 2;

  REGION = 'REGION';
  SUBREGION = 'SUBREGION';
  MKTAREA = 'MKTAREA';
  PLNGMKT = 'PLNGMKT';
  MKTDIV = 'MKTDIV';

  regionOptions = new SelectableOptions();
  geographyOptions = new SelectableOptions();
  plngMktOptions = new SelectableOptions();
  mktDivOptions = new SelectableOptions();

  startYear: number;
  endYear: number;
  yearRange: number[];

  constructor(readonly vehicleScenarioService: VehicleScenarioService,
              readonly reportService: ReportService,
              readonly stateService: StateService,
              readonly excelService: ExcelExportService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {

  }

  ngOnInit(): void {
    this.selectedGeography = this.SUBREGION_FLAG;
    // If there is already a selected scenario within the state, then use that selected scenario
    if (this.stateService.isVehicleScenarioSelected) {
      this.workingScenario = this.stateService.selectedScenario;
      this.compareScenario = this.stateService.selectedCompareScenario;
    }
    if (this.workingScenario) {
      this.getFilterOptions();
    }
    this.getYearRange();
  }

  /**
   * Event handler for when the run report button is clicked
   */
  onRunReport(): void {
    if (this.isRptByPlngMkt) {
      this.dialog.open(FabConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Confirmation',
          message: 'Report By Planning Market will result in 3 report worksheets per Planning Market, continue?',
          noLabel: 'Cancel',
          yesLabel: 'Confirm',
          titleIcon: 'warning',
          actionColor: 'primary'
        },
        disableClose: true
      })
      .afterClosed().subscribe({
        next: confirm => {
          if (confirm) {
            this.runReport();
          }
        }
      });
    } else {
      this.runReport();
    }
  }

  /**
   * Exports the report
   */
  runReport(): void {
    this.showSpinner = true;
    // Generate request
    const request = new VarianceMktDivSgmntRptRequest();
    // If By Planning Market checkbox is checked, the planning market list will be a list of the planning markets. If it is false,
    // then the planning market list will just be a list with one value, where the value is one string of comma separated planning markets
    const plngMkt = this.isRptByPlngMkt ? this.plngMktOptions.selectedOptions : [this.plngMktOptions.selectedOptions.join(',')]
    request.workingScenId = this.workingScenario.vehScenId;
    request.workingScenName = this.workingScenario.vehScenName;
    request.compareScenId = this.compareScenario.vehScenId;
    request.compareScenName = this.compareScenario.vehScenName;
    request.isBySubregion = this.selectedGeography === this.SUBREGION_FLAG;
    request.subregionOrMktAreas = this.geographyOptions.selectedOptions;
    request.planningMarkets = plngMkt;
    request.mktDivs = this.mktDivOptions.selectedOptions;
    request.gmid = this.stateService.getUserGmid();
    request.startYear = this.startYear;
    request.endYear = this.endYear;
    request.isByPlngMktOutput = this.isRptByPlngMkt;

    // Export the report
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const scenarioString = this.workingScenario.vehScenId + '-' + this.compareScenario.vehScenId;
    const fileName = 'VarianceMktDivSgmnt_' + scenarioString + '_' + currDate;
    this.reportService.getVarianceByMktDivOrSgmntRpt(request).subscribe({
      next: (response: any) => {
        this.excelService.exportToExcelFromServer(response, fileName);
      },
      error: (error: any) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Downloaded Successfully', DISMISS_MSG);
      }
    });
  }

  /**
   * Event handler for when the Geography type changes
   */
  onGeographyChange(): void {
    // Clear out the filter data and re-fetch the filter data
    this.resetAllFilterOptions();
    this.resetAllSelectedFilter();
    this.getFilterOptions();
  }

  /**
   * Gets the filter options for the report
   */
  getFilterOptions(): void {
    this.showSpinner = false;
    this.vehicleScenarioService.getFiltersForQueryVehicle(this.stateService.getUserGmid(), this.workingScenario.vehScenId).subscribe({
      next: (data) => {
        this.allFilterOptions = data;
        this.filteredData = data;
      },
      error: (err) => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.initializeFilterOptions();
      }
    });
  }

  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * Event handler whenever a filter changes
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.filteredData = this.allFilterOptions;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
    // Update the filtered data based off of which filters are selected
    this.applyFilter(this.REGION, this.regionOptions.selectedOptions);
    if (this.selectedGeography === this.SUBREGION_FLAG) {
      this.applyFilter(this.SUBREGION, this.geographyOptions.selectedOptions);
    } else if (this.selectedGeography === this.MKTAREA_FLAG) {
      this.applyFilter(this.MKTAREA, this.geographyOptions.selectedOptions);
    }
    this.applyFilter(this.MKTDIV, this.mktDivOptions.selectedOptions);
    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }

  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    this.regionOptions.options = this.setFilterOption(this.REGION)
    if (this.selectedGeography === this.SUBREGION_FLAG) {
      this.geographyOptions.options = this.setFilterOption(this.SUBREGION);
      this.setCascadingFilterOptions(this.SUBREGION);
    } else if (this.selectedGeography === this.MKTAREA_FLAG) {
      this.geographyOptions.options = this.setFilterOption(this.MKTAREA);
      this.setCascadingFilterOptions(this.MKTAREA);
    }
  }

  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        this.initializeFilterOptions();
      // eslint-disable-next-line
      case this.SUBREGION:
        this.plngMktOptions.options = this.setFilterOption(this.PLNGMKT);
        this.plngMktOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.MKTAREA:
        this.plngMktOptions.options = this.setFilterOption(this.PLNGMKT);
        this.plngMktOptions.selectedOptions = [];
      // eslint-disable-next-line
      case this.PLNGMKT:
        this.mktDivOptions.options = this.setFilterOption(this.MKTDIV);
        this.mktDivOptions.selectedOptions = [];
    }
  }

  /**
   * Resets the output filters for the pivot table data
   */
  resetAllFilterOptions(): void {
    // Clear the filter options
    this.geographyOptions.options = [];
    this.plngMktOptions.options = [];
    this.mktDivOptions.options = [];
  }

  /**
   * Resets all the selected filters from the filter dropdowns
   */
  resetAllSelectedFilter(): void {
    // Clear the selected filters
    this.geographyOptions.selectedOptions = [];
    this.plngMktOptions.selectedOptions = [];
    this.mktDivOptions.selectedOptions = [];
  }

  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }

  /**
   * Opens the pop-up to select a vehicle scenario
   */
  selectScenario = (): void => {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiselect: false,
        title: 'Select Vehicle Scenario',
      }
    });
    dialogRef.afterClosed().subscribe(async res => {
      if (res) {
        this.workingScenario = res.selectedScenarios[0];
        this.compareScenario = this.workingScenario;
        this.getYearRange();
        this.getFilterOptions();
      }
    });
  }

  /**
   * Opens the SelectCompareScenario pop up component
   */
  selectCompareScenario(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        validFcorgs: [this.workingScenario.fcorg],
        title: 'Select Compare Scenario',
        isMultiselect: false,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.compareScenario = res.selectedScenarios[0];
      }
    });
  }

  /**
   * return whether the run report button should be enabled or disabled
   */
  isRunReportEnabled(): boolean {
    return this.workingScenario && this.compareScenario && this.geographyOptions.selectedOptions.length > 0
      && this.plngMktOptions.selectedOptions.length > 0 && this.mktDivOptions.selectedOptions.length > 0
      && this.startYear > 0 && this.endYear > 0;
  }
}
